// TypeScript recreation of the CoffeeScript toCurrency function
// and NumberHelpers.number_to_currency functionality

import { toI18n } from 'util/i18n';

/** Configuration options for currency formatting */
export interface CurrencyOptions {
  /** Number of decimal places to display (default: 2) */
  precision?: number;
  /** Currency symbol (default: '$' or from I18n) */
  unit?: string;
  /** Character between whole and decimal part (default: '.') */
  separator?: string;
  /** Thousands delimiter (default: ',') – falsy to disable */
  delimiter?: string;
  /** Where to position the currency unit (default: 'start') */
  unit_position?: 'start' | 'end';
}

/**
 * Insert thousands delimiters into a number string.
 * TypeScript equivalent of NumberHelpers.number_with_delimiter
 */
const numberWithDelimiter = (
  value: string | number,
  opts: Pick<CurrencyOptions, 'separator' | 'delimiter'> = {}
): string => {
  const separator = opts.separator ?? '.';
  const delimiter = opts.delimiter ?? ',';

  const [integerPart, decimalPart = ''] = value.toString().split('.');

  let integer = integerPart;
  if (delimiter) {
    integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
  }

  return `${integer}${decimalPart ? separator : ''}${decimalPart}`;
};

/**
 * Format a number as currency with proper delimiters and symbols.
 * TypeScript equivalent of NumberHelpers.number_to_currency
 */
const numberToCurrency = (
  value: number | string,
  opts: CurrencyOptions = {}
): string => {
  const precision = opts.precision ?? 2;
  const unit = opts.unit ?? '$';
  const separator = opts.separator ?? '.';
  const delimiter = opts.delimiter ?? ',';
  const unitPosition = opts.unit_position ?? 'start';

  // Early return for non-numeric values (preserves original behavior)
  if (isNaN(Number(value))) {
    return value as string;
  }

  const num = parseFloat(String(value));
  const sign = num < 0 ? '-' : ''; // Inline ternary

  const fixedWidth = Math.abs(num).toFixed(precision);
  const delimited = numberWithDelimiter(fixedWidth, { delimiter });
  const result = delimited.split('.').join(separator);

  // Single return with ternary for unit positioning
  return unitPosition === 'end'
    ? `${sign}${result}${unit}`
    : `${sign}${unit}${result}`;
};

/**
 * Format a value as currency with locale-aware defaults.
 *
 * This function recreates the original CoffeeScript `@toCurrency` behavior:
 * - If the input already starts with the currency unit, return it unchanged
 * - Otherwise, format it using proper currency formatting rules
 * - Uses I18n for locale-appropriate currency symbols
 *
 * @param value - The numeric value to format, or pre-formatted string
 * @param opts - Formatting options (precision, unit, separators, etc.)
 * @returns Formatted currency string
 *
 * @example
 * ```typescript
 * toCurrency(1234.56)                    // "$1,234.56"
 * toCurrency("$1,234.56")               // "$1,234.56" (unchanged)
 * toCurrency(1234.56, { unit: '€' })    // "€1,234.56"
 * toCurrency(1234.56, {
 *   delimiter: '.',
 *   separator: ','
 * })                                    // "$1.234,56" (European style)
 * ```
 */
export const toCurrency = (
  value: number | string | null | undefined,
  opts: CurrencyOptions = {}
): string => {
  // Set default unit from I18n if not provided, with fallback for safety
  opts.unit ??= toI18n('number.currency.format.unit') || '$';

  // Check if value already starts with currency unit (mirrors CoffeeScript: number?[0])
  const stringValue = value?.toString();
  if (stringValue?.[0] === opts.unit) {
    return stringValue!; // Non-null assertion: we know it's defined due to the condition
  }

  // Otherwise, format using currency rules
  return numberToCurrency(value as number | string, opts);
};
